import * as React from "react";
import { Alert, Box, Stack, TextField } from "@mui/material";
import CclDrawer from "../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../components/common/cclButtons/cclLoadingButton";
import { useUpdateDocumentMutation } from "../../services/cclTokenedAssessmentApi";
import { DocumentItem, UpdateDocumentRequest } from "../../services/types/assessmentAPITypes";

export interface EditDocumentDrawerProps {
    open: boolean;
    document: DocumentItem | undefined;
    onClose: () => void;
    onSaveComplete: (documentId: number, newTitle: string) => void;
}

const EditDocumentDrawer: React.FC<EditDocumentDrawerProps> = (props) => {
    const [editedTitle, setEditedTitle] = React.useState<string>("");
    const [saveError, setSaveError] = React.useState<boolean>(false);
    const [updateDocument, { isLoading: saving }] = useUpdateDocumentMutation();

    React.useEffect(() => {
        if (props.open && props.document) {
            setEditedTitle(props.document.documentName);
            setSaveError(false);
        }
    }, [props.open, props.document]);

    const canSave =
        editedTitle.trim() !== "" && editedTitle.trim() !== props.document?.documentName;

    const handleSave = () => {
        if (!canSave || !props.document) return;
        const request: UpdateDocumentRequest = {
            documentId: props.document.documentId,
            body: { newDocumentName: editedTitle.trim() },
        };
        updateDocument(request)
            .unwrap()
            .then(() => {
                props.onSaveComplete(props.document!.documentId, editedTitle.trim());
                props.onClose();
            })
            .catch(() => {
                setSaveError(true);
            });
    };

    return (
        <CclDrawer title="Edit Document" open={props.open} onClose={props.onClose}>
            <Box sx={{ overflow: "hidden", height: 1 }}>
                <Box sx={{ overflow: "auto" }}>
                    <Stack sx={{ m: 1, mt: 2 }} spacing={4}>
                        <TextField
                            size="small"
                            label="Document Title"
                            value={editedTitle}
                            onChange={(e) => {
                                setEditedTitle(e.target.value);
                                setSaveError(false);
                            }}
                            fullWidth
                        />
                        {saveError && (
                            <Alert severity="error">
                                Failed to update document title. Please try again.
                            </Alert>
                        )}
                    </Stack>
                </Box>
                <CclDrawerActionBar>
                    <CclLoadingButton
                        onClick={handleSave}
                        disabled={!canSave}
                        mode={"primary"}
                        loading={saving}
                    >
                        Save
                    </CclLoadingButton>
                    <CclButton onClick={props.onClose} mode={"secondary"}>
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Box>
        </CclDrawer>
    );
};

export default EditDocumentDrawer;
